/**
 * Creates custom console with tracking time capabilities.
 *
 * @param {*} envNameOrLogLevel production/development or debug,info,warn,error.
 */
export function createCustomConsole(
  envNameOrLogLevel = 'development',
  originalConsole = window.console,
  isDebugEnabled = false
) {
  //Feat: verbose level for debug messages
  let debugVerboseLevel = () =>
    parseInt(localStorage.getItem('debugVerboseLevel')) ||
    window.debugVerboseLevel ||
    1

  //Feat: scoped debug messages
  let debugVerboseScopes = []
  const getDebugVerboseScopes = () => {
    return [
      ...debugVerboseScopes, //state
      ...(window.debugVerboseScopes || []), //dynamic
      //from cache
      ...(localStorage.getItem('debugVerboseScopes') || '')
        .split(',')
        .filter((str) => !!str && str.toString() !== 'null'),
    ]
  }
  const isDebugVerboseScopeEnabled = (scopeName) => {
    return (
      getDebugVerboseScopes().includes(scopeName) ||
      getDebugVerboseScopes().includes('*')
    )
  }

  /**
   * Fallback to debug
   */
  let logLevel = ['production', 'development'].includes(envNameOrLogLevel)
    ? {
        production: 'warn',
        development: 'debug',
      }[envNameOrLogLevel]
    : ['error', 'warn', 'debug'].includes(envNameOrLogLevel)
    ? envNameOrLogLevel
    : 'debug'

  const isDebugEnabledFn = () => {
    if (window.debug === true || localStorage.getItem('debug') === '1') {
      return true
    }
    return isDebugEnabled || logLevel === 'debug'
  }

  originalConsole.log(
    'Logging custom console log level:',
    logLevel,
    'verbose level',
    debugVerboseLevel(),
    'verbose scopes',
    getDebugVerboseScopes()
  )

  /**
   * Custom console
   */
  let newConsole = {
    getLogLevel: () => logLevel,
    debugVerboseLevel,
    setLogLevel: (level) => (logLevel = level),
    setDebugVerboseLevel: (level) => (window.debugVerboseLevel = level),
    isDebugVerboseScopeEnabled,
    getDebugVerboseScopes,
    /**
     *
     * @param {Array|string} scopes e.g cache,api or ['cache', 'api']
     * @returns
     */
    setDebugVerboseScope: (scopes = []) =>
      (debugVerboseScopes =
        scopes instanceof Array ? scopes : scopes.split(',')),
    ...originalConsole,
    oc: originalConsole,
    isDebugEnabled: isDebugEnabledFn,
    fatal() {
      if (['error', 'warn', 'info', 'debug'].includes(logLevel)) {
        originalConsole.error.apply(originalConsole, [].slice.call(arguments))
      }
    },
    error() {
      if (['error', 'warn', 'info', 'debug'].includes(logLevel)) {
        originalConsole.error.apply(originalConsole, [].slice.call(arguments))
      }
    },
    warn() {
      if (['warn', 'info', 'debug'].includes(logLevel)) {
        originalConsole.warn.apply(originalConsole, [].slice.call(arguments))
      }
    },
    info() {
      if (['info', 'debug'].includes(logLevel)) {
        originalConsole.info.apply(originalConsole, [].slice.call(arguments))
      }
    },
    debug() {
      if (['debug'].includes(logLevel) || isDebugEnabledFn()) {
        originalConsole.debug.apply(originalConsole, [].slice.call(arguments))
      }
    },
    /**
     * Same as a normal debug, but we can set a level so that, for example, we can decide
     * later to only print debug messages with level <= 5.
     * @param {number} level Verbose level 1..10
     * @param  {...any} args
     */
    debugVerbose(level, ...args) {
      if (level <= debugVerboseLevel()) {
        newConsole.debug.apply(newConsole, [`${level}: `, ...args])
      }
    },
    /**
     * Same as debugVerbose but with scopes, so that, for example, we can decide
     * later to only print debug messages from a certain feature.
     *
     * Usage:
     *
     * localStorage.debugVerboseScopes = 'api'
     * localStorage.debugVerboseLevel = 1
     * console.debugVerboseScope(1,'api','Hi foo')
     * //Hi foo
     *
     * console.debugVerboseScope(1,'other','Hi bar')
     * //(no output)
     *
     * @param {string} scope Scope name e.g containers
     * @param {number} level Verbose level 1..10
     * @param  {...any} args
     */
    debugVerboseScope(scope, level, ...args) {
      //feat: Support inversing scope/level params
      if (typeof scope === 'number' && typeof level === 'string') {
        let _level = scope
        scope = level
        level = _level
      }

      let isScopeWhitelisted = false
      if (typeof scope === 'string') {
        isScopeWhitelisted = isDebugVerboseScopeEnabled(scope)
      } else {
        //feat: Scope can be an array of scopes
        if (scope instanceof Array) {
          isScopeWhitelisted = scope.some((item) => {
            return isDebugVerboseScopeEnabled(item)
          })
        }
      }

      if (level <= debugVerboseLevel() && isScopeWhitelisted) {
        newConsole.debug.apply(newConsole, [`${level} ${scope}: `, ...args])
      }
    },
    log() {
      if (['debug'].includes(logLevel)) {
        originalConsole.log.apply(originalConsole, [].slice.call(arguments))
      }
    },
    table() {
      originalConsole.log.apply(originalConsole, [].slice.call(arguments))
    },
    /**
     * Function to track the time taken by a part of code
     *
     * @param {string} text Text to log
     * @param {boolean|string|number|Array} enabled Flag to enable/disable tracking
     * @returns {object} Scope object with count and stop method
     */
    trackTime(text = 'trackTime', enabled = true) {
      const state = {}

      let logMethod = console.log

      //Feat: limit to verbose-level/scope-name
      let [verboseLevel, verboseScope] = [false, false]
      if (enabled instanceof Array && enabled.length > 0) {
        verboseLevel = enabled[0]
        verboseScope = enabled[1] || ''
        enabled = typeof verboseLevel === 'number' ? verboseLevel : enabled
        logMethod = function () {
          console.debugVerboseScope.apply(console, [
            verboseScope,
            verboseLevel,
            ...arguments,
          ])
        }
      }

      //Usage e.g: enabled = 8
      //Limit to certain verbose level
      if (typeof enabled === 'number') {
        enabled = enabled <= debugVerboseLevel()
      }

      //Usage e.g: enabled = [1,'cacheScope']
      if (typeof verboseScope === 'string') {
        enabled = verboseScope
      }

      //Resolve the second argument as the scope name (debugVerboseScopes)
      //Limit to certain verbose scope
      if (typeof enabled === 'string') {
        enabled = isDebugVerboseScopeEnabled(enabled)
      }

      if (!['debug'].includes(logLevel) && !isDebugEnabledFn()) {
        enabled = false //Disable on prod envs
      }

      const logger = {
        startTime: Date.now(),
        trackTime: function () {
          const endTime = Date.now()
          const elapsedTime = endTime - logger.startTime
          const hours = Math.floor(elapsedTime / 3600000)
          const minutes = Math.floor((elapsedTime % 3600000) / 60000)
          const seconds = Math.floor((elapsedTime % 60000) / 1000)
          const ms = Math.floor(elapsedTime % 1000)
          const str = `Logging Elapsed (${text}) elapsed: ${hours}:${minutes}:${seconds}:${ms}`
          logMethod(str, state)
          return str
        },
        stop: function () {
          return logger.trackTime()
        },
      }
      if (enabled) {
        logMethod(`Logging Start (${text})`, state)
      }
      let scope = function stop() {
        if (!enabled) {
          return
        }
        return logger.stop()
      }
      scope.stop = () => scope()

      /**
       * Log/Counter function. Using the same uniqueId e.g requestCount, will act like a counter. If you call 100 times it will store 100. Sample is just an object attachment overriden each time.
       * @param {string} uniqueId
       * @param {Object} sample Optional sample attached to txt
       * @returns
       */
      scope.count = (uniqueId, sample = {}) => {
        if (!enabled) {
          return
        }
        state[uniqueId] = state[uniqueId] || 0
        state[uniqueId]++
        state[uniqueId + '_sample'] = state[uniqueId + '_sample'] || sample
      }
      return scope
    },
  }
  return newConsole
}

/**
 * Show a message in the center of the screen. Auto-hides after a few seconds.
 *
 * @param {*} message
 */
export function alertDOM(message) {
  if (window.__alertData && window.__alertData.alertContainer) {
    window.__alertData.alertContainer.textContent = message
    clearTimeout(window.__alertData.timeoutId)
  } else {
    const alertContainer = document.createElement('div')
    alertContainer.textContent = message
    Object.assign(alertContainer.style, {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: '#fff',
      color: '#000',
      padding: '10px',
      border: '1px solid #000',
      zIndex: '9999999999', // Set a very high z-index value
    })

    document.body.appendChild(alertContainer)

    window.__alertData = {
      alertContainer,
      timeoutId: null,
    }
  }

  window.__alertData.timeoutId = setTimeout(() => {
    window.__alertData.alertContainer.remove()
    window.__alertData.alertContainer = null
    window.__alertData = null
  }, 3000) // Remove the alert after 3 seconds (adjust as needed)
}

window._alertDOM = alertDOM
